import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import type { ProductType, FavoriteItem } from '../api/use-favorites'

export interface UseToggleFavoriteResult {
  isFavorite: boolean
  isLoading: boolean
  toggle: () => void
}

interface ToggleFavoriteResponse {
  success: boolean
  data: {
    added: boolean
    item?: {
      id: string
      productType: ProductType
      productId: string
    }
  }
}

interface FavoritesListResponse {
  success: boolean
  data: FavoriteItem[]
}

/**
 * Хук для работы с избранным.
 *
 * Использует единый запрос GET /api/user/favorites вместо
 * индивидуального /check per product — React Query дедуплицирует
 * одинаковый queryKey, поэтому 50 карточек дают ровно 1 HTTP-запрос.
 */
export function useToggleFavorite(
  productId: string,
  productType: ProductType = 'tea',
): UseToggleFavoriteResult {
  const { initData, hapticFeedback } = useTelegram()
  const queryClient = useQueryClient()

  // Один запрос на все карточки — кэш ['favorites'] общий
  const { data: favoritesData } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => apiClient<FavoritesListResponse>('/api/user/favorites', { initData }),
    enabled: !!initData,
    staleTime: 30 * 1000,
  })

  const isFavorite = favoritesData?.data?.some(
    (item) => item.productId === productId && item.productType === productType,
  ) ?? false

  const toggleMutation = useMutation({
    mutationFn: async () => {
      if (!initData) throw new Error('Требуется авторизация')
      return apiClient<ToggleFavoriteResponse>('/api/user/favorites/toggle', {
        method: 'POST',
        body: JSON.stringify({ productType, productId }),
        initData,
      })
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['favorites'] })
      const previous = queryClient.getQueryData<FavoritesListResponse>(['favorites'])

      if (previous) {
        const alreadyIn = previous.data.some(
          (item) => item.productId === productId && item.productType === productType,
        )
        queryClient.setQueryData<FavoritesListResponse>(['favorites'], {
          ...previous,
          data: alreadyIn
            ? previous.data.filter(
                (item) => !(item.productId === productId && item.productType === productType),
              )
            : [
                ...previous.data,
                {
                  id: `optimistic-${productId}`,
                  userId: 0,
                  productType,
                  productId,
                  createdAt: new Date().toISOString(),
                },
              ],
        })
      }

      hapticFeedback.impactOccurred('light')
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['favorites'], context.previous)
      }
      hapticFeedback.notificationOccurred('error')
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
      if (data.data.added) {
        hapticFeedback.notificationOccurred('success')
      }
    },
  })

  return {
    isFavorite,
    isLoading: toggleMutation.isPending,
    toggle: () => {
      if (!toggleMutation.isPending) toggleMutation.mutate()
    },
  }
}
